import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '../../context/TasksContext';
import { taskErrorMessage } from '../../utils/taskApi';
import { TaskList } from '../../types/tasks';
import TaskDialog from './TaskDialog';
import TaskIcon from './TaskIcon';
import '../../tasks.css';

export interface RequestPreset {
  title?: string;
  lines?: string[];
  /** Set when asked for from an input's row: links the request to that input. */
  inputId?: number;
}

interface RequestDialogProps {
  warehouses: { id: number; name: string }[];
  preset?: RequestPreset;
  onClose: () => void;
}

/**
 * Warehouse "Custom Request" (Inputs page). Saving makes a list the requester
 * can follow on the Lists page, and puts the request on the MPP week list for
 * the admin set in Admin › Notifications (api/tasks.ts createRequest).
 */
const RequestDialog: React.FC<RequestDialogProps> = ({ warehouses, preset, onClose }) => {
  const { createRequest } = useTasks();
  const navigate = useNavigate();
  const [title, setTitle] = useState(preset?.title || '');
  const [lines, setLines] = useState<string[]>(preset?.lines?.length ? preset.lines : ['']);
  const [warehouseId, setWarehouseId] = useState<number | null>(warehouses[0]?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<TaskList | null>(null);
  const lineRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setLine = (index: number, value: string) => setLines((prev) => prev.map((l, i) => (i === index ? value : l)));

  const addLine = (after: number) => {
    setLines((prev) => [...prev.slice(0, after + 1), '', ...prev.slice(after + 1)]);
    window.setTimeout(() => lineRefs.current[after + 1]?.focus(), 0);
  };

  const removeLine = (index: number) => {
    setLines((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
    window.setTimeout(() => lineRefs.current[Math.max(0, index - 1)]?.focus(), 0);
  };

  const save = async () => {
    if (!title.trim()) {
      setError('Give the request a title.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const list = await createRequest({
        title: title.trim(),
        lines: lines.map((l) => l.trim()).filter(Boolean),
        warehouse_id: warehouseId,
        input_id: preset?.inputId ?? null,
      });
      setSent(list);
    } catch (e) {
      setError(taskErrorMessage(e, 'Could not send the request'));
    } finally {
      setSaving(false);
    }
  };

  if (sent) {
    return (
      <TaskDialog
        title="Request sent"
        onClose={onClose}
        footer={
          <>
            <button type="button" className="tk-btn tk-btn-ghost" onClick={onClose}>Done</button>
            <button type="button" className="tk-btn tk-btn-primary" onClick={() => navigate(`/lists?list=${sent.id}`)}>
              <TaskIcon name="list" size={15} /> View in Lists
            </button>
          </>
        }
      >
        <p className="rq-sent">
          <TaskIcon name="check" size={18} /> <strong>{sent.title}</strong> has been added as a list.
        </p>
        <p className="tk-muted">
          You can follow it on the <strong>Lists</strong> page. MPP has been notified, and you'll get an email when
          someone leaves a note on it and when it's been completed.
        </p>
      </TaskDialog>
    );
  }

  return (
    <TaskDialog
      title="Custom Request"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="tk-btn tk-btn-primary" onClick={save} disabled={saving || !title.trim()}>
            {saving ? 'Sending…' : 'Send request'}
          </button>
        </>
      }
    >
      <div className="rq-form">
        <label className="tk-field">
          <span className="tk-field-label">Title <span className="rq-required">(required)</span></span>
          <input
            className="tk-input"
            autoFocus
            value={title}
            maxLength={200}
            placeholder="e.g. More bolts needed for Gadget"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                lineRefs.current[0]?.focus();
              }
            }}
          />
        </label>

        {warehouses.length > 1 && (
          <label className="tk-field">
            <span className="tk-field-label">For warehouse</span>
            <select className="tk-input" value={warehouseId ?? ''} onChange={(e) => setWarehouseId(Number(e.target.value) || null)}>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
        )}

        <div className="tk-field">
          <span className="tk-field-label">Items</span>
          <span className="tk-muted rq-items-hint">
            The first item is what MPP sees on their weekly list, with the rest one click away.
          </span>
          <ul className="rq-lines">
            {lines.map((line, index) => (
              <li key={index} className="rq-line">
                <span className="rq-bullet" aria-hidden="true" />
                <input
                  ref={(el) => (lineRefs.current[index] = el)}
                  className="tk-input"
                  value={line}
                  maxLength={2000}
                  placeholder={index === 0 ? 'e.g. 500x Bolts ASAP' : 'Another item'}
                  onChange={(e) => setLine(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addLine(index);
                    } else if (e.key === 'Backspace' && !line && lines.length > 1) {
                      e.preventDefault();
                      removeLine(index);
                    }
                  }}
                  aria-label={`Item ${index + 1}`}
                />
                <button
                  type="button"
                  className="tk-icon-btn"
                  onClick={() => removeLine(index)}
                  aria-label={`Remove item ${index + 1}`}
                  disabled={lines.length === 1 && !line}
                >
                  <TaskIcon name="close" size={14} />
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm rq-add" onClick={() => addLine(lines.length - 1)}>
            <TaskIcon name="plus" size={14} /> Add item
          </button>
        </div>

        <p className="tk-muted rq-hint">
          This is saved as its own list you can follow on the <strong>Lists</strong> page, and added to MPP's weekly
          list. You'll be emailed about notes on it and when it's completed.
        </p>
        {error && <p className="error-text">{error}</p>}
      </div>
    </TaskDialog>
  );
};

export default RequestDialog;
